import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { clone } from 'mobx-state-tree';
import { css } from '@emotion/core';
import { withTheme } from 'emotion-theming';
import DocumentTitle from 'react-document-title';
import { FormattedMessage } from 'react-intl';
import { text1, text2 } from '../../../constants/classes';
import ItemImage from '../../common/ItemImage';
import ItemInfo from './ItemInfo';
import NavButtonGroup from '../../../components/common/NavButtonGroup';
import Textarea from '../../common/Textarea';
import compareValues from '../../../utils/compare-values';
import Dropdown from '../../common/Dropdown';
import { STATUS_TITLES, getAllowedStatuses } from '../../../constants/orders';
import { NotesAndSchemeWrapper, Notes, ItemHeaderContainer, ItemHeader } from './styled';
import {
  Container,
  FlexWrapper,
  ItemPhotoWrapper,
  ItemDetails,
  ItemName,
  ActionButton,
  ItemInfoContainer,
} from '../../common/SingleItem';
import { title } from '../../../constants';
import OrderTimeline from './OrderTimeline';

const { failed } = STATUS_TITLES;

const LAST = 'last';
const FIRST = 'first';
const PREVIOUS = -1;
const NEXT = 1;

const { Option } = Dropdown;

@withTheme
@inject('routingStore')
@inject('ordersStore')
@observer
class SingleOrdersItem extends Component {
  constructor(props) {
    super(props);
    this.state = {
      singleItem: undefined,
      sourceItem: undefined,
      editableFormula: false,
      // loading: false
    };
    this.validateIfNavBtnIsDisabled = this.validateIfNavBtnIsDisabled.bind(this);
    this.validateIfSameData = this.validateIfSameData.bind(this);
    this.validateErrorsInData = this.validateErrorsInData.bind(this);
    this.updateComponent = this.updateComponent.bind(this);
    this.handleOrderUpdate = this.handleOrderUpdate.bind(this);
  }

  componentDidMount() {
    this.getSingleItem();
  }

  componentDidUpdate(prevState) {
    const {
      params: { code },
    } = this.props;
    if (prevState.params.code !== code) {
      this.getSingleItem();
    }
  }

  setItemToStateAndFetchAll(item) {
    const {
      ordersStore: { fetchAllCheckedItems },
    } = this.props;
    this.setState({ singleItem: clone(item), sourceItem: clone(item) }, () =>
      fetchAllCheckedItems(),
    );
  }

  getSingleItem() {
    const {
      ordersStore,
      params: { code },
    } = this.props;
    if (this.ifOrderFetched()) {
      this.setItemToStateAndFetchAll(ordersStore.getSingleItem(code));
    } else {
      this.fetchSingleItem();
    }
  }

  fetchSingleItem() {
    const {
      ordersStore: { fetchSingleItemData },
      params,
    } = this.props;
    fetchSingleItemData(params.code).then(item => this.setItemToStateAndFetchAll(item));
  }

  ifOrderFetched() {
    const {
      ordersStore: { getSingleItem },
      params: { code },
    } = this.props;
    return getSingleItem(code) !== undefined;
  }

  validateIfNavBtnIsDisabled(place) {
    const {
      params: { code },
      ordersStore: { checkedOrdersArray },
    } = this.props;
    const index = place === LAST ? checkedOrdersArray.length - 1 : 0;
    return checkedOrdersArray[index] === code || !checkedOrdersArray.length;
  }

  validateIfSameData() {
    const { singleItem, sourceItem } = this.state;
    return compareValues(singleItem, sourceItem);
  }

  validateErrorsInData() {
    const {
      singleItem: { invalidSourceTrackingUrl },
    } = this.state;
    return invalidSourceTrackingUrl;
  }

  updateComponent() {
    const { ordersStore, params } = this.props;
    this.setState({
      editableFormula: false,
      singleItem: clone(ordersStore.getSingleItem(params.code)),
    });
  }

  checkIfSourceOrderIsFailed() {
    const { ordersStore, params } = this.props;
    return ordersStore.getSingleItem(params.code)?.status === failed;
  }

  handleOrderUpdate() {
    const {
      ordersStore: { updateSingleOrder, getSingleItem },
      params,
    } = this.props;
    const { singleItem } = this.state;
    singleItem.parseAllPricing();
    this.setState({ editableFormula: false });
    updateSingleOrder(singleItem).catch(() =>
      this.setState({ singleItem: clone(getSingleItem(params.code)) }),
    );
  }

  handleGoToListing = (actualSingleItem, browserHistory) => {
    if (!actualSingleItem?.listingItemId) return;
    // eslint-disable-next-line consistent-return
    return browserHistory.push(`/listings/single-item/${actualSingleItem?.listingItemId}`);
  };

  render() {
    const {
      routingStore: { browserHistory },
      headingId,
      ordersStore,
      params,
      theme,
    } = this.props;
    const {
      switchItem,
      handleReturn,
      currentLanguage,
      // saveAndTryAgain,
      failedReasons,
      singleItemLoading,
    } = ordersStore;
    const { singleItem, editableFormula } = this.state;
    const { code } = params;
    const isFailedOrder = singleItem?.status === failed;
    const titleColor = isFailedOrder ? theme.colors.pinkDark : null;
    const actualSingleItem = ordersStore.getSingleItem(code);

    return (
      <DocumentTitle title={singleItem ? `${title} | ${singleItem.title}` : title}>
        <Container>
          {singleItem ? (
            <Fragment>
              <h1
                css={css`
                  ${text1};
                  margin-right: 17px;
                `}
              >
                <FormattedMessage id={`orders.${headingId}`} />
              </h1>
              <NavButtonGroup
                disabledPrevious={this.validateIfNavBtnIsDisabled(FIRST)}
                previousAction={() => switchItem(code, PREVIOUS)}
                returnAction={handleReturn}
                disabledNext={this.validateIfNavBtnIsDisabled(LAST)}
                nextAction={() => switchItem(code, NEXT)}
                css={css`
                  margin-bottom: 14px;
                `}
              />
              <FlexWrapper>
                <ItemPhotoWrapper>
                  <ItemImage style={{ maxWidth: '282px' }} mainImage={singleItem.imageUrl} />
                </ItemPhotoWrapper>
                <ItemDetails>
                  <ItemName>
                    <ItemHeaderContainer>
                      <ItemHeader color={titleColor}>
                        <FormattedMessage id="orders.systemOrderId" /> : {singleItem.systemCode}
                      </ItemHeader>
                      <ItemHeader marginLeft="30px">
                        <FormattedMessage id="orders.statuses" />:
                      </ItemHeader>
                      <Dropdown
                        width="140px"
                        value={singleItem.status}
                        onChange={singleItem.setStatus}
                        disabled={actualSingleItem && actualSingleItem.status === 'inProcess'}
                        css={css`
                          margin-left: 10px;
                        `}
                      >
                        {actualSingleItem &&
                          getAllowedStatuses(actualSingleItem.status).map(({ status, id }) => (
                            <Option key={id} value={status}>
                              <FormattedMessage id={`orders.${status}`} />
                            </Option>
                          ))}
                      </Dropdown>
                      {isFailedOrder ? (
                        <Fragment>
                          <ItemHeader marginLeft="30px">
                            <FormattedMessage id="orders.failedReason" />
                          </ItemHeader>
                          <Dropdown
                            width="200px"
                            value={singleItem.failedReason}
                            onChange={singleItem.setFailedReason}
                            css={css`
                              margin-left: 10px;
                            `}
                          >
                            {failedReasons.map(reason => (
                              <Option key={reason} value="reason">
                                {reason}
                              </Option>
                            ))}
                          </Dropdown>
                        </Fragment>
                      ) : null}
                    </ItemHeaderContainer>
                    <a
                      onClick={() =>
                        browserHistory.push(
                          `/listings/single-item/${actualSingleItem.listingItemId}`,
                        )
                      }
                    >
                      <h3
                        css={css`
                          ${text2};
                          font-weight: bold;
                          color: ${titleColor};
                          cursor: pointer;
                        `}
                      >
                        {singleItem.title}
                      </h3>
                    </a>
                  </ItemName>
                  <FlexWrapper>
                    <ItemInfoContainer width="50%">
                      <ItemInfo
                        code={code}
                        data={singleItem}
                        currentLanguage={currentLanguage}
                        editableFormula={editableFormula}
                        cancelEditFormula={() => this.setState({ editableFormula: false })}
                        editFormula={() => this.setState({ editableFormula: true })}
                        goToListing={() => this.handleGoToListing(actualSingleItem, browserHistory)}
                      />
                      <div>
                        <ActionButton
                          htmlType="submit"
                          clickHandler={this.handleOrderUpdate}
                          disabled={
                            singleItemLoading ||
                            !!(this.validateIfSameData() || this.validateErrorsInData())
                          }
                        >
                          <FormattedMessage id="common.save" />
                        </ActionButton>
                        <ActionButton
                          type="secondary"
                          clickHandler={this.updateComponent}
                          disabled={this.validateIfSameData()}
                        >
                          <FormattedMessage id="common.cancel" />
                        </ActionButton>
                        {/* <ActionButton */}
                        {/*   clickHandler={() => saveAndTryAgain(singleItem)} */}
                        {/*   type="secondary" */}
                        {/*   disabled={!this.checkIfSourceOrderIsFailed() || isFailedOrder} */}
                        {/* > */}
                        {/*   <FormattedMessage id="common.saveTryAgain" /> */}
                        {/* </ActionButton> */}
                      </div>
                    </ItemInfoContainer>
                    <NotesAndSchemeWrapper>
                      <OrderTimeline singleItem={singleItem} />
                      <Notes>
                        <h3
                          css={css`
                            ${text2};
                            text-align: left;
                          `}
                        >
                          <FormattedMessage id="orders.notes" />
                          <FormattedMessage id="orders.typeNote">
                            {placeholder => (
                              <Textarea
                                onChange={singleItem.setNote}
                                value={singleItem.note || ''}
                                placeholder={singleItem.note ? '' : placeholder}
                                ordersNotes
                                css={text2}
                              />
                            )}
                          </FormattedMessage>
                        </h3>
                      </Notes>
                    </NotesAndSchemeWrapper>
                  </FlexWrapper>
                </ItemDetails>
              </FlexWrapper>
            </Fragment>
          ) : null}
        </Container>
      </DocumentTitle>
    );
  }
}

SingleOrdersItem.propTypes = {
  headingId: PropTypes.string.isRequired,
  routingStore: PropTypes.object,
  params: PropTypes.object.isRequired,
  ordersStore: PropTypes.object,
  theme: PropTypes.object,
};

export default SingleOrdersItem;
